import { Injectable } from '@angular/core';
import { AppConstants } from '@utils/app.constants';
import {
  LMFieldUtilService,
  LMFormatUtilService,
} from './utils';
import * as _ from 'lodash';

@Injectable()
export class LMCompoundFieldService {

  constructor(
    private lmFieldUtilService: LMFieldUtilService,
    private lmFormatUtilService: LMFormatUtilService
  ) { }

  // ported from landmarkGroupField.js
  public flattenLayout(layout: LMFormLayout | Array<LMFormLayoutSection>): LMFormLayoutElements {
    const subFields: Array<LMFormLayoutElement> = [];
    const layoutObj = (layout.hasOwnProperty('sections'))
      ? (layout as LMFormLayout).sections as Array<LMFormLayoutSection>
      : layout as Array<LMFormLayoutSection>;

    layoutObj.forEach((section: LMFormLayoutSection) => {
      if (section.columns) {
        this.flattenColumnsForGroupField(section, subFields);

      } else if (section.paragraph) {
        this.flattenParagraphForGroupField(section.paragraph, subFields);

      } else if (section.field) {
        subFields.push({ field: section.field });

      } else {
        // do nothing
      }
    });

    return { elements: subFields };
  }

  // Form version is parseGroupFieldMemberValue. Analogous changes
  // must be made to both, until one method is developed.
  public setGroupFieldMemberValue(dataView: LMDataView, dvField: LMField): void {
    this._setGroupFieldMemberValue(dataView, dvField, dvField.value);
  }

  // private methods
  private _setGroupFieldMemberValue(dataView: LMDataView, dvField: LMField, compactValue: any): string {
    const delimiter = dvField.delimiter || '';
    const members = dvField.members;
    let memberField;

    for (let i = 0, length = members.length; i < length; i++) {
      if (compactValue.length === 0) {
        // Clear the remaining members
        for (let j = i; j < length; j++) {
          memberField = dataView.fields[members[j]];
          memberField.value = this.lmFieldUtilService.toBlankValue();
        }
        break;
      }

      memberField = dataView.fields[dvField.members[i]];

      if (this.lmFormatUtilService.isCompoundField(memberField)) {
        // TODO - is this necessary?
        compactValue = this._setGroupFieldMemberValue(dataView, memberField, compactValue);
      } else {
        if (delimiter.length > 0) {
          const index = compactValue.indexOf(delimiter);
          const newValue = ((index === -1) ? compactValue : compactValue.substr(0, index));

          memberField.value = newValue;
          compactValue = (index === -1) ? '' : compactValue.substr(index + 1);
        } else {
          const newValue = (compactValue.substr(0, memberField.size));

          memberField.value = newValue;
          compactValue = compactValue.substr(memberField.size);
        }
      }
    }

    return compactValue;
  }

  private flattenColumnsForGroupField(section: LMFormLayoutSection, subFields: Array<LMFormLayoutElement>) {
    // for 4.0, columns are always an array
    section.columns.forEach((column) => {
      column.elements.forEach((element) => {
        this.addGroupFieldSubField(element, subFields);
      })
    });
  }

  private flattenParagraphForGroupField(paragraph: LMFormLayoutParagraph, subFields: Array<LMFormLayoutElement>) {
    paragraph.elements.forEach((element) => {
      this.addGroupFieldSubField(element, subFields);
    })
  }

  private addGroupFieldSubField(element: LMFormLayoutElements, subFields: Array<LMFormLayoutElement>) {
    if (element.field) {
      subFields.push({ field: element.field });
    }
  }

  public computeCompactFieldValue(dataView: LMDataView, fieldId: string, specFields?: LMSpecFields): string {
    const dvField: LMField = dataView.fields[ fieldId ];
    return this._computeCompactFieldValue(dataView, dvField, true, specFields);
  }

  // NOTE: method is recursive
  private _computeCompactFieldValue(dataView: LMDataView, dvField: LMField, checkBlank: boolean, specFields?: LMSpecFields): string {
    let delimiter = dvField.delimiter || '';
    let returnValue = '';
    let addDelimiter = false;
    let allFieldsBlank = true;

    dvField.members.forEach((item, index, ary) => {
      const memberField: LMField = dataView.fields[ item ];
      const type = memberField.type || '';

      if (addDelimiter) {
        returnValue += delimiter;
      }

      if (type === 'array' || type === 'group') {
        delimiter = memberField.delimiter || '';
        addDelimiter = (delimiter.length > 0);

        memberField.value = this._computeCompactFieldValue(dataView, memberField, false, specFields);

        allFieldsBlank = (allFieldsBlank && memberField.value.length === 0);
        returnValue += memberField.value;

      } else {
        const value = memberField.value;

        if (specFields && specFields.hasOwnProperty(item)) {
          const specField: LMSpecField = specFields[ item ];

          if (specField.type === AppConstants.BINARYDOCUMENT) {
            // TODO: issue: check if linkValue is still used for 4.0
            // value = memberField.linkValue;
          }

          allFieldsBlank = (allFieldsBlank && this.lmFieldUtilService.isBlank(specField.type, value));
        }

        if (delimiter.length === 0) {
          returnValue += this.getFieldPaddedValue(memberField);

        } else {
          addDelimiter = true;
          returnValue += _.trim(value.toString());
        }
      }
    });

    if (specFields && allFieldsBlank && checkBlank) {
      return '';
    }

    // trim trailing delimiters
    let newLength = returnValue.length;
    const delimLength = delimiter.length;

    for (; newLength > 0 && delimLength > 0 ; newLength -= delimLength) {
      if (delimiter !== (returnValue.substring(newLength - delimLength, newLength))) {
        break;
      }
    }

    returnValue = returnValue.substr(0, newLength);
    return returnValue;
  }

  private getFieldPaddedValue(dvField: LMField): string {
    let value = dvField.value.toString();
    const length = value.length;

    if (length < dvField.size) {
      for (let index = length; index < dvField.size; index++) {
        value += ' ';
      }

    } else {
      value = value.substr(0, dvField.size);
    }

    return value;
  }
}
